import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Header from "@/components/Header";
import { X } from "lucide-react";
import { getAuthSession, getValidAuthSession, saveSessionFromUrl } from "@/lib/supabaseAuth";
import { fetchSupabaseRows, updateSupabaseRows } from "@/lib/supabaseRest";
import { formatPrice } from "@/utils/price";

const stateLabels = {
  publicada: "Publicada",
  requiere_revision_manual: "Revision manual",
  pendiente_revision: "Pendiente",
  pausada: "Pausada",
  rechazada: "Rechazada",
  eliminada: "Eliminada",
};

const getUserFriendlyAlerts = (property) => {
  const alerts = Array.isArray(property.revision_alertas)
    ? property.revision_alertas
    : [];
  const messages = [];

  if (property.estado_publicacion === "requiere_revision_manual") {
    messages.push("Tu propiedad está en proceso de revisión por Tulocalmx.");
  }

  alerts.forEach((alert) => {
    const text = String(alert || "").toLowerCase();

    if (text.includes("duplicado")) {
      messages.push(
        "Detectamos una propiedad parecida. La revisaremos para evitar publicaciones repetidas."
      );
      return;
    }

    if (text.includes("croquis") || text.includes("plano")) {
      messages.push("Croquis o plano pendiente. No bloquea la publicación.");
      return;
    }

    if (text.includes("video")) {
      messages.push(
        "Video exterior e interior recomendado. No bloquea la publicación, pero ayuda a rentar más rápido."
      );
      return;
    }

    if (text.includes("telefono")) {
      messages.push(
        "Está en revisión porque el teléfono parece incorrecto. Puedes editarlo para que podamos publicarla."
      );
      return;
    }

    if (text.includes("descripcion")) {
      messages.push("La descripción necesita más detalle para publicarse mejor.");
      return;
    }

    if (text.includes("foto")) {
      messages.push("Necesitamos más fotos para publicar con buena calidad.");
    }
  });

  return [...new Set(messages)];
};

const MisPropiedades = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState(() => getAuthSession());
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [updatingId, setUpdatingId] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const loadProperties = async () => {
    const urlSession = await saveSessionFromUrl(true);
    const currentSession = urlSession || (await getValidAuthSession());
    setSession(currentSession);

    if (!currentSession?.access_token) {
      setIsLoading(false);
      setAuthChecked(true);
      navigate("/iniciar-sesion");
      return;
    }

    const query = new URLSearchParams({
      select: "*",
      order: "created_at.desc",
      estado_publicacion: "neq.eliminada",
    });
    if (currentSession.user?.id) {
      query.set("user_id", `eq.${currentSession.user.id}`);
    }

    try {
      setLoadError("");
      const rows = await fetchSupabaseRows("property_submissions", query.toString(), {
        accessToken: currentSession.access_token,
      });
      if (isMounted) setProperties(rows);
    } catch (error) {
        console.log("No se pudieron cargar tus propiedades:", error);
        if (isMounted) {
          setLoadError(
            "Supabase bloqueó la lectura de tus propiedades. Ejecuta el SQL del portal de usuarios para activar user_id y las políticas RLS de lectura."
          );
        }
    } finally {
      if (isMounted) {
        setIsLoading(false);
        setAuthChecked(true);
      }
    }
    };

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const updatePublicationState = async (property, nextState) => {
    setUpdatingId(property.id);

    try {
      await updateSupabaseRows(
        "property_submissions",
        `id=eq.${encodeURIComponent(property.id)}`,
        {
          estado_publicacion: nextState,
          publicada_en:
            nextState === "publicada" ? new Date().toISOString() : property.publicada_en,
          updated_at: new Date().toISOString(),
          notas_admin:
            nextState === "pausada"
              ? "Pausada por el anunciante desde su portal."
              : "Reanudada por el anunciante desde su portal.",
        },
        { accessToken: session.access_token }
      );

      setProperties((prev) =>
        prev.map((item) =>
          item.id === property.id
            ? { ...item, estado_publicacion: nextState }
            : item
        )
      );
    } catch (error) {
      alert("No se pudo actualizar la publicacion. Intenta de nuevo.");
    } finally {
      setUpdatingId("");
    }
  };

  const deleteProperty = async () => {
    if (!deleteTarget) return;

    setIsDeleting(true);

    try {
      await updateSupabaseRows(
        "property_submissions",
        `id=eq.${encodeURIComponent(deleteTarget.id)}`,
        {
          estado_publicacion: "eliminada",
          updated_at: new Date().toISOString(),
          notas_admin: "Eliminada por el anunciante desde su portal.",
        },
        { accessToken: session.access_token }
      );

      setProperties((prev) => prev.filter((item) => item.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      alert("No se pudo eliminar la propiedad. Intenta de nuevo.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!session?.access_token && !authChecked) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-slate-50 px-4 py-24">
          <div className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <p className="font-black text-slate-950">Cargando tu portal...</p>
            <p className="mt-2 text-sm text-slate-600">
              Estamos verificando tu sesion.
            </p>
          </div>
        </main>
      </>
    );
  }

  if (!session?.access_token) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-slate-50 px-4 py-24">
          <div className="mx-auto max-w-xl rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm">
            <p className="font-black text-slate-950">Abriendo tu cuenta...</p>
            <p className="mt-2 text-sm text-slate-600">
              Si no avanza, inicia sesion para ver tus propiedades.
            </p>
            <button
              type="button"
              onClick={() => navigate("/iniciar-sesion")}
              className="mt-4 rounded-lg bg-[#F97316] px-4 py-3 text-sm font-black text-white"
            >
              Iniciar sesion
            </button>
          </div>
        </main>
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-slate-50 px-4 py-24">
        <div className="mx-auto max-w-6xl">
          <div className="relative mb-5 flex flex-col gap-3 pr-12 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-wide text-[#F97316]">
                Portal de anunciantes
              </p>
              <h1 className="text-2xl font-black text-slate-950">
                Mis propiedades
              </h1>
              <p className="mt-1 text-sm text-slate-600">
                Administra tus espacios publicados y en revision.
              </p>
            </div>
            <div className="flex gap-2">
              <Link to="/registro?plan=gratis" className="rounded-lg bg-[#F97316] px-4 py-3 text-sm font-black text-white">
                Nueva propiedad
              </Link>
              <button
                type="button"
                onClick={() => navigate("/mi-perfil")}
                aria-label="Regresar a Mi cuenta"
                className="absolute right-0 top-0 flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {location.state?.propertyUpdateMessage && (
            <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-bold text-emerald-800">
              {location.state.propertyUpdateMessage}
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
            {isLoading ? (
              <p className="p-5 text-sm font-semibold text-slate-500">Cargando...</p>
            ) : loadError ? (
              <div className="p-8 text-center">
                <p className="font-black text-slate-950">No se pudieron cargar tus propiedades.</p>
                <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600">
                  {loadError}
                </p>
              </div>
            ) : properties.length === 0 ? (
              <div className="p-8 text-center">
                <p className="font-black text-slate-950">Todavía no tienes propiedades.</p>
                <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-600">
                  Si ya habías subido propiedades antes de crear tu cuenta, hay que asignarlas a tu usuario en Supabase.
                </p>
                <Link to="/registro?plan=gratis" className="mt-4 inline-flex rounded-lg bg-[#F97316] px-4 py-3 text-sm font-black text-white">
                  Publicar mi primera propiedad
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {properties.map((property) => (
                  <div key={property.id} className="grid gap-3 p-4 sm:grid-cols-[96px_1fr_auto] sm:items-center">
                    <img
                      src={property.imagenes?.[0] || ""}
                      alt=""
                      className="h-24 w-24 rounded-lg bg-slate-100 object-cover"
                    />
                    <div>
                      <p className="font-black text-slate-950">
                        {property.tipo} - {formatPrice(property.precio)}
                      </p>
                      <p className="mt-1 text-sm text-slate-600">{property.ubicacion}</p>
                      <p className="mt-1 text-xs font-bold uppercase tracking-wide text-slate-500">
                        {stateLabels[property.estado_publicacion] || property.estado_publicacion}
                      </p>
                      {getUserFriendlyAlerts(property).length > 0 && (
                        <p className="mt-2 text-xs leading-5 text-orange-700">
                          {getUserFriendlyAlerts(property).join(" ")}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {property.estado_publicacion === "publicada" && (
                        <Link
                          to={`/propiedad/supabase-${property.id}`}
                          state={{ fromOwnerPortal: true }}
                          className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-black text-[#0056D2]"
                        >
                          Ver publicacion
                        </Link>
                      )}
                      <Link
                        to={`/editar-propiedad/${property.id}`}
                        className="rounded-lg bg-[#F97316] px-3 py-2 text-sm font-black text-white"
                      >
                        Editar
                      </Link>
                      {property.estado_publicacion === "requiere_revision_manual" && (
                        <span className="flex basis-full text-xs font-semibold text-slate-500 sm:basis-auto">
                          Corrige los datos marcados para volver a revisarla.
                        </span>
                      )}
                      {property.estado_publicacion === "publicada" && (
                        <button
                          type="button"
                          disabled={updatingId === property.id}
                          onClick={() => updatePublicationState(property, "pausada")}
                          className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-black text-slate-700"
                        >
                          Pausar
                        </button>
                      )}
                      {property.estado_publicacion === "pausada" && (
                        <button
                          type="button"
                          disabled={updatingId === property.id}
                          onClick={() => updatePublicationState(property, "publicada")}
                          className="rounded-lg bg-emerald-600 px-3 py-2 text-sm font-black text-white"
                        >
                          Reanudar
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={updatingId === property.id}
                        onClick={() => setDeleteTarget(property)}
                        className="rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-black text-red-600"
                      >
                        Eliminar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-red-600">
                  Eliminar propiedad
                </p>
                <h2 className="mt-1 text-xl font-black text-slate-950">
                  ¿Estás seguro?
                </h2>
              </div>
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50"
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-4 text-sm leading-6 text-slate-600">
              Si borras esta propiedad, dejará de aparecer en tu panel y en Tulocalmx.
              La publicación se perderá para los interesados.
            </p>
            <div className="mt-5 rounded-xl bg-slate-50 p-3 text-sm font-bold text-slate-700">
              {deleteTarget.tipo} - {formatPrice(deleteTarget.precio)}
              <span className="mt-1 block font-semibold text-slate-500">
                {deleteTarget.ubicacion}
              </span>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg border border-slate-200 px-4 py-3 text-sm font-black text-slate-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                disabled={isDeleting}
                onClick={deleteProperty}
                className="rounded-lg bg-red-600 px-4 py-3 text-sm font-black text-white disabled:bg-red-300"
              >
                {isDeleting ? "Eliminando..." : "Sí, eliminar propiedad"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MisPropiedades;

